// pages/AuthCallback.tsx
import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, CircularProgress, Typography, Button } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as userApi from '../../../../api/user';
import { theme } from '../../../../theme';
import { authBus } from '../../domain/authBus';
import { getNextFromQuery, popNextUrl } from '../../domain/authNavigation';

interface AuthCallbackProps {
  onSuccess?: () => void;
  redirectTo?: string;
}

const AuthCallback: React.FC<AuthCallbackProps> = ({ onSuccess, redirectTo = '/' }) => {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'idle' | 'loading' | 'ok' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    const code = search.get('code');

    if (!code) {
      setStatus('error');
      setErrorMsg('Missing authentication code in the URL.');
      return;
    }

    const go = async () => {
      setStatus('loading');
      setErrorMsg(null);

      try {
        // ✅ comportamento original: conclui auth chamando o backend
        await userApi.subscriberAuth(code);

        // (opcional) “esquenta” sessão/cookies
        try {
          await userApi.session();
        } catch {
          // ignore
        }

        // avisa o app que login concluiu (se você ainda usa isso)
        try {
          authBus.emit('login');
        } catch {
          // ignore
        }

        setStatus('ok');

        // ✅ VOLTA pro /g/{guid} (salvo quando acessou /g/{guid}/auth)
        const next = getNextFromQuery(search) ?? popNextUrl() ?? redirectTo;
        onSuccess?.();
        navigate(next, { replace: true });
      } catch (e: any) {
        setStatus('error');

        const backendError = e?.response?.data?.error || e?.response?.data?.message || e?.message;
        const msg =
          e?.response?.status === 401
            ? 'Invalid or expired code.'
            : backendError
              ? `Authentication failed: ${backendError}`
              : 'Failed to complete authentication.';

        setErrorMsg(msg);
      }
    };

    void go();
  }, [search, onSuccess, navigate, redirectTo]);

  return (
    <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Card
        sx={{
          width: 420,
          bgcolor: theme.palette.background.paper,
          border: `2px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary,
          borderRadius: 2,
          boxShadow: 8,
        }}
      >
        <CardContent sx={{ textAlign: 'center' }}>
          {status === 'loading' && (
            <>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Connecting…</Typography>
            </>
          )}

          {status === 'error' && (
            <>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Authentication failed.
              </Typography>
              <Typography sx={{ mb: 2 }}>{errorMsg}</Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/', { replace: true })}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  '&:hover': { backgroundColor: theme.palette.primary.main, opacity: 0.9 },
                  mr: 1,
                }}
              >
                Back
              </Button>
              <Button variant="outlined" onClick={() => navigate('/login', { replace: true })}>
                Sign in options
              </Button>
            </>
          )}

          {status === 'ok' && <Typography>Successfully authenticated. Redirecting…</Typography>}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AuthCallback;
